import React from 'react';
import { Query, withApollo } from 'react-apollo';
import PropTypes from 'prop-types';
import PENDING from './PendingQueries/pendingQueries.graphql';

// Define the root nodes which should not be cached
const DYNAMIC_QUERY_NODES = [
  'viewer',
  'findHotelOrder',
  'hotelOrder',
  'hotels',
];

/**
 * This function probably belongs in the schema repo
 *
 * @param {Object} queryAst a AST Object representing a graphql Document
 * @returns {Boolean}
 */
const isDynamicQuery = ({ definitions }) => {
  // Returns a flat array of all query fields
  const queryFields = definitions
    .filter(({ operation }) => operation === 'query')
    .map(({ selectionSet }) => selectionSet)
    .reduce((flatArr, { selections }) => flatArr.concat(selections), []);

  const dynamicQueryFields = queryFields.filter(({ name }) =>
    DYNAMIC_QUERY_NODES.includes(name.value)
  );

  return dynamicQueryFields.length > 0;
};

/**
 * SSRAwareQuery
 *
 * This component is a drop-in replacement for `react-apollo`'s `Query`.
 *
 * It exists to help avoid the inadvertent inclusion of private information and queries which should be dynamic (i.e. not cached) in SSR rendered output.
 * It does this by inspecting the query AST passed in the `query` prop and testing it for root query nodes which should not be included in SSR rendered output.
 *
 * Should the query be found to contain such data we will, in addition, set the fetchPolicy to `network-only` to ensure that the
 * rendered data will always be consistent with the backend.
 *
 * It may be that as the schema evolves more conditions will have to be evaluated in this simple wrapper.
 * To add more conditions add the root node name to the `DYNAMIC_QUERY_NODES` array.
 *
 */
export const SSRAwareQuery = ({ query, children, client, ...restProps }) => {
  const isSSR = !isDynamicQuery(query);

  React.useEffect(() => {
    return () => {
      client.writeQuery({
        query: PENDING,
        data: {
          pendingQueries: [],
        },
      });
    };
  }, [client]);

  return (
    <Query
      query={query}
      {...restProps}
      ssr={isSSR}
      fetchPolicy={isSSR ? undefined : 'network-only'}
    >
      {children}
    </Query>
  );
};

SSRAwareQuery.propTypes = {
  query: PropTypes.object.isRequired,
  children: PropTypes.func,
  client: PropTypes.object.isRequired,
};

export default withApollo(SSRAwareQuery);
